import React, { useCallback, useMemo } from 'react';
import {
    Box,
    IconButton,
    Slider,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    alpha,
} from '@mui/material';
import { PlayArrow, Pause } from '@mui/icons-material';
import { vortxColors, vortxTypography, useThemeMode } from '@vortx/shared-ui';

const STEPS_PER_HOUR = 12; // 5-minute intervals

/**
 * Format a 5-min time index to "h:mm AM/PM" display string.
 */
function formatTimeIndex(index) {
    const h = Math.floor(index / STEPS_PER_HOUR);
    const m = (index % STEPS_PER_HOUR) * 5;
    const period = h < 12 ? 'AM' : 'PM';
    let display = h % 12;
    if (display === 0) display = 12;
    return `${display}:${String(m).padStart(2, '0')} ${period}`;
}

/**
 * Slider marks: every 3 hours with labels at 12A, 6A, 12P, 6P.
 */
const SLIDER_MARKS = [0, 3, 6, 9, 12, 15, 18, 21].map((h) => ({
    value: h * STEPS_PER_HOUR,
    label: { 0: '12A', 6: '6A', 12: '12P', 18: '6P' }[h] || undefined,
}));

const SPEED_OPTIONS = [0.5, 1, 2];

/**
 * TimeControls - Playback controls for a 24-hour depot simulation animation.
 *
 * Supports both 5-minute resolution (288 steps) and legacy hourly (24 steps).
 *
 * @param {Object} props
 * @param {number} props.currentTimeIndex - Current time index (0-287 for 5-min, 0-23 for hourly)
 * @param {number} props.totalSteps       - Total steps (288 for 5-min, 24 for hourly)
 * @param {boolean} props.isPlaying       - Whether auto-play is active
 * @param {number} props.speed            - Playback speed: 0.5, 1, or 2
 * @param {Function} props.onTimeChange   - Called when scrubber moves (index: number) => void
 * @param {Function} props.onPlayPause    - Toggle play/pause () => void
 * @param {Function} props.onSpeedChange  - Change playback speed (speed: number) => void
 */
function TimeControls({
    currentTimeIndex,
    totalSteps = 288,
    isPlaying,
    speed,
    onTimeChange,
    onPlayPause,
    onSpeedChange,
}) {
    const { isRetro } = useThemeMode();
    const isFiveMin = totalSteps > 24;

    const handleSliderChange = useCallback(
        (_event, value) => {
            onTimeChange(value);
        },
        [onTimeChange]
    );

    const handleSpeedChange = useCallback(
        (_event, newSpeed) => {
            if (newSpeed !== null) {
                onSpeedChange(newSpeed);
            }
        },
        [onSpeedChange]
    );

    const clockText = useMemo(() => {
        if (isFiveMin) {
            return formatTimeIndex(currentTimeIndex);
        }
        // Legacy hourly format
        const h = currentTimeIndex % 24;
        const period = h < 12 ? 'AM' : 'PM';
        let display = h % 12;
        if (display === 0) display = 12;
        return `${display}:00 ${period}`;
    }, [currentTimeIndex, isFiveMin]);

    const clockFontFamily = isRetro
        ? "'JetBrains Mono', 'Consolas', monospace"
        : "'Inter', sans-serif";

    const sliderMarks = isFiveMin
        ? SLIDER_MARKS
        : Array.from({ length: 24 }, (_, i) => ({
              value: i,
              label: { 0: '12A', 6: '6A', 12: '12P', 18: '6P' }[i] || undefined,
          }));

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5, md: 2 },
                px: { xs: 1.5, sm: 2, md: 3 },
                py: 1.5,
                bgcolor: isRetro ? '#1e1e1e' : '#FFFFFF',
                borderTop: `1px solid ${isRetro
                    ? alpha(vortxColors.teal, 0.25)
                    : alpha(vortxColors.teal, 0.15)
                }`,
                boxShadow: isRetro
                    ? '0 -2px 8px rgba(0,0,0,0.3)'
                    : '0 -1px 4px rgba(0,0,0,0.06)',
            }}
        >
            {/* Play / Pause button */}
            <IconButton
                onClick={onPlayPause}
                size="medium"
                aria-label={isPlaying ? 'Pause simulation' : 'Play simulation'}
                sx={{
                    color: isPlaying ? vortxColors.gold : 'text.secondary',
                    transition: 'color 0.2s ease',
                    '&:hover': {
                        bgcolor: alpha(vortxColors.gold, 0.12),
                    },
                }}
            >
                {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>

            {/* Time scrubber slider */}
            <Slider
                value={currentTimeIndex}
                onChange={handleSliderChange}
                min={0}
                max={totalSteps - 1}
                step={1}
                marks={sliderMarks}
                aria-label="Simulation time"
                sx={{
                    flex: 1,
                    minWidth: 120,
                    mx: 1,
                    color: vortxColors.teal,
                    '& .MuiSlider-rail': {
                        bgcolor: alpha(vortxColors.gold, 0.3),
                        height: 4,
                    },
                    '& .MuiSlider-track': {
                        bgcolor: vortxColors.teal,
                        height: 4,
                        border: 'none',
                    },
                    '& .MuiSlider-thumb': {
                        bgcolor: vortxColors.gold,
                        width: 14,
                        height: 14,
                        '&:hover, &.Mui-focusVisible': {
                            boxShadow: `0 0 0 6px ${alpha(vortxColors.gold, 0.2)}`,
                        },
                        '&.Mui-active': {
                            boxShadow: `0 0 0 8px ${alpha(vortxColors.gold, 0.3)}`,
                        },
                    },
                    '& .MuiSlider-mark': {
                        bgcolor: alpha(
                            isRetro ? vortxColors.white : vortxColors.charcoal,
                            0.2
                        ),
                        width: 2,
                        height: 6,
                        borderRadius: 1,
                    },
                    '& .MuiSlider-markActive': {
                        bgcolor: alpha(vortxColors.teal, 0.5),
                    },
                    '& .MuiSlider-markLabel': {
                        fontSize: '0.65rem',
                        color: isRetro ? vortxColors.stone : 'text.secondary',
                        top: 28,
                    },
                }}
            />

            {/* Clock display */}
            <Typography
                component="span"
                {...(isRetro ? { 'data-retro-mono': 'true' } : {})}
                sx={{
                    fontFamily: clockFontFamily,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: isRetro ? vortxColors.gold : vortxColors.charcoal,
                    minWidth: 88,
                    textAlign: 'center',
                    letterSpacing: '0.02em',
                    userSelect: 'none',
                    ...(isRetro && {
                        textShadow: '0 0 8px rgba(178, 140, 80, 0.6)',
                    }),
                }}
            >
                {clockText}
            </Typography>

            {/* Speed toggle buttons */}
            <ToggleButtonGroup
                value={speed}
                exclusive
                onChange={handleSpeedChange}
                size="small"
                aria-label="Playback speed"
                sx={{
                    '& .MuiToggleButton-root': {
                        px: { xs: 0.8, sm: 1.2 },
                        py: 0.4,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        fontFamily: vortxTypography.body,
                        color: isRetro ? vortxColors.stone : 'text.secondary',
                        borderColor: isRetro
                            ? alpha(vortxColors.teal, 0.3)
                            : alpha(vortxColors.teal, 0.2),
                        transition: 'all 0.2s ease',
                        '&.Mui-selected': {
                            bgcolor: alpha(vortxColors.gold, 0.15),
                            color: vortxColors.gold,
                            borderColor: alpha(vortxColors.gold, 0.4),
                            fontWeight: 700,
                            '&:hover': {
                                bgcolor: alpha(vortxColors.gold, 0.22),
                            },
                        },
                        '&:hover': {
                            bgcolor: alpha(vortxColors.gold, 0.08),
                        },
                    },
                }}
            >
                {SPEED_OPTIONS.map((s) => (
                    <ToggleButton key={s} value={s} aria-label={`${s}x speed`}>
                        {s}x
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>
    );
}

export default TimeControls;
